import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TiptapEditor from '../components/TiptapEditor';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Plus, Edit2, Trash2, LogOut, FileText, Eye, BookOpen, Lightbulb, Mail, Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resources'); // 'resources' or 'teacher-tips'
  const [resources, setResources] = useState([]);
  const [teachingTips, setTeachingTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [contentType, setContentType] = useState('resource'); // 'resource' or 'teaching-tip'
  const [uploadedFiles, setUploadedFiles] = useState([]); // For multiple file uploads
  
  // Resource notification state
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationResource, setNotificationResource] = useState(null);
  const [selectedAudience, setSelectedAudience] = useState([]);
  const [notificationPreview, setNotificationPreview] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Resource form data
  const [resourceData, setResourceData] = useState({
    title: '',
    description: '',
    category: 'worksheet',
    grade_level: 'Year 4',
    subject: '',
    publisher: '',
    chapter_topic: '',
    is_premium: false,
    file_url: '',
    tags: []
  });

  // Teaching Tip form data
  const [tipData, setTipData] = useState({
    title: '',
    featured_image: '',
    content: '',
    excerpt: '',
    category: 'classroom_ideas',
    subject: '',
    grade_range: 'All Grades',
    reading_time: 5,
    is_featured: false
  });

  const [uploading, setUploading] = useState(false);

  // Debug useEffect to check when tipData changes
  useEffect(() => {
    if (editingItem && contentType === 'teaching-tip') {
      console.log('📝 Editing article:', {
        article_id: editingItem.article_id,
        title: editingItem.title,
        contentLength: editingItem.content?.length || 0,
        tipDataContentLength: tipData.content?.length || 0
      });
    }
  }, [editingItem, tipData, contentType]);

  const resourceCategories = [
    { value: 'worksheet', label: 'Worksheet' },
    { value: 'test_paper', label: 'Test Paper' },
    { value: 'lesson_plan', label: 'Lesson Plan' },
    { value: 'teaching_notes', label: 'Teaching Notes' },
    { value: 'reading_resource', label: 'Reading Resource' },
    { value: 'quiz', label: 'Quiz' }
  ];

  const tipCategories = [
    { value: 'classroom_ideas', label: 'Classroom Ideas' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'differentiation', label: 'Differentiation' },
    { value: 'ai_for_teachers', label: 'AI for Teachers' },
    { value: 'parent_tips', label: 'Parent Tips' },
    { value: 'behaviour_management', label: 'Behaviour Management' },
    { value: 'student_motivation', label: 'Student Motivation' },
    { value: 'inclusive_education', label: 'Inclusive Education' },
    { value: 'creative_teaching', label: 'Creative Teaching' },
    { value: 'teacher_productivity', label: 'Teacher Productivity' }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'resources') {
        const response = await axios.get(`${API}/resources`, { withCredentials: true });
        setResources(response.data);
      } else {
        const response = await axios.get(`${API}/teaching-tips`, { withCredentials: true });
        setTeachingTips(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate all files
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }

      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|docx|doc|png|jpg|jpeg|webp)$/i)) {
        toast.error(`${file.name} - Only PDF, DOCX, and image files are allowed`);
        return;
      }
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      files.forEach(file => {
        formDataUpload.append('files', file);
      });

      const response = await axios.post(`${API}/upload-multiple`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (contentType === 'resource') {
        // MERGE newly uploaded files with existing files (for Edit mode)
        const newFiles = response.data.files;
        console.log('📁 Upload successful:', {
          newFilesCount: newFiles.length,
          newFiles: newFiles,
          prevFilesCount: uploadedFiles.length
        });
        
        const mergedFiles = [...uploadedFiles, ...newFiles];
        console.log('📁 Setting uploadedFiles to:', {
          mergedFilesCount: mergedFiles.length,
          mergedFiles: mergedFiles
        });
        
        setUploadedFiles(mergedFiles);
        toast.success(`${files.length} file(s) uploaded successfully!`);
      } else {
        setTipData({ ...tipData, featured_image: response.data.files[0]?.url || '' });
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CRITICAL: Prevent submission while files are still uploading
    if (uploading) {
      toast.error('Please wait for file upload to complete');
      return;
    }
    
    try {
      if (contentType === 'resource') {
        // Prepare resource data with ALL uploaded files
        const dataToSend = {
          ...resourceData,
          files: uploadedFiles,  // Include all uploaded files
          file_url: uploadedFiles.length > 0 ? uploadedFiles[0].url : ''  // First file as legacy primary
        };
        
        // DEBUG: Log the payload
        console.log('🔍 CREATE/UPDATE Payload:', {
          title: dataToSend.title,
          filesCount: dataToSend.files.length,
          files: dataToSend.files,
          isEdit: !!editingItem,
          uploadingState: uploading
        });
        
        if (editingItem) {
          await axios.put(
            `${API}/resources/${editingItem.resource_id}`,
            dataToSend,
            { withCredentials: true }
          );
          toast.success('Resource updated successfully');
        } else {
          await axios.post(`${API}/resources`, dataToSend, { withCredentials: true });
          toast.success('Resource created successfully');
        }
      } else {
        if (editingItem) {
          await axios.put(
            `${API}/teaching-tips/${editingItem.article_id}`,
            tipData,
            { withCredentials: true }
          );
          toast.success('Teaching tip updated successfully');
        } else {
          await axios.post(`${API}/teaching-tips`, tipData, { withCredentials: true });
          toast.success('Teaching tip created successfully');
        }
      }
      
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      if (type === 'resource') {
        await axios.delete(`${API}/resources/${id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/teaching-tips/${id}`, { withCredentials: true });
      }
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (type, item) => {
    console.log('🔧 handleEdit called', { type, itemId: item.article_id || item.resource_id });
    setEditingItem(item);
    setContentType(type);
    
    if (type === 'resource') {
      setResourceData({
        title: item.title,
        description: item.description,
        category: item.category,
        grade_level: item.grade_level,
        subject: item.subject || '',
        publisher: item.publisher || '',
        chapter_topic: item.chapter_topic || '',
        is_premium: item.is_premium || false,
        file_url: item.file_url || '',
        tags: item.tags || []
      });
      // Preserve existing files array from the resource
      setUploadedFiles(item.files || []);
    } else {
      console.log('📝 Setting tipData for article:', item.article_id, 'Content length:', item.content?.length);
      setTipData({
        title: item.title,
        featured_image: item.featured_image || '',
        content: item.content || '',
        excerpt: item.excerpt || '',
        category: item.category,
        subject: item.subject || '',
        grade_range: item.grade_range || 'All Grades',
        reading_time: item.reading_time || 5,
        is_featured: item.is_featured || false
      });
      setTimeout(() => console.log('✅ Modal should be open with content'), 100);
    }
    console.log('🔓 Opening modal, showModal will be true');
    setShowModal(true);
  };

  const resetForm = () => {
    console.log('🔄 resetForm called');
    setResourceData({
      title: '',
      description: '',
      category: 'worksheet',
      grade_level: 'Year 4',
      subject: '',
      publisher: '',
      chapter_topic: '',
      is_premium: false,
      file_url: '',
      tags: []
    });
    setUploadedFiles([]);
    setTipData({
      title: '',
      featured_image: '',
      content: '',
      excerpt: '',
      category: 'classroom_ideas',
      subject: '',
      grade_range: 'All Grades',
      reading_time: 5,
      is_featured: false
    });
  };
  
  // Resource notification handlers
  const openNotificationModal = (resource) => {
    setNotificationResource(resource);
    setSelectedAudience([]);
    setNotificationPreview(null);
    setShowNotificationModal(true);
  };
  
  const handleAudienceToggle = (tagId) => {
    setSelectedAudience(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };
  
  const previewNotification = async () => {
    if (selectedAudience.length === 0) {
      toast.error('Please select at least one audience');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/resources/broadcast/preview`,
        {
          resource_id: notificationResource.resource_id,
          tag_ids: selectedAudience,
          preview_only: true
        },
        { withCredentials: true }
      );
      
      setNotificationPreview(response.data);
      toast.success('Preview generated!');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };
  
  const sendNotification = async () => {
    if (selectedAudience.length === 0) {
      toast.error('Please select at least one audience');
      return;
    }
    
    if (!window.confirm(`Send notification to ${selectedAudience.length} audience group(s)?`)) {
      return;
    }
    
    setSendingNotification(true);
    try {
      const response = await axios.post(
        `${API}/resources/broadcast/send`,
        {
          resource_id: notificationResource.resource_id,
          tag_ids: selectedAudience
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setShowNotificationModal(false);
      setSelectedAudience([]);
      setNotificationPreview(null);
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const openAddModal = (type) => {
    resetForm();
    setEditingItem(null);
    setContentType(type);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-5xl sm:text-6xl font-bold tracking-tight leading-tight text-gray-900 mb-2">
                Admin Dashboard
              </h1>
              <p className="text-base sm:text-lg leading-relaxed text-gray-600">
                Manage your resources and teaching tips
              </p>
            </div>
            
            <button
              onClick={handleLogout}
              className="bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300 rounded-full font-bold px-6 py-3 inline-flex items-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b-2 border-gray-200">
            <button
              onClick={() => setActiveTab('resources')}
              className={`pb-4 px-6 font-semibold transition-all ${
                activeTab === 'resources'
                  ? 'text-[#0EA5E9] border-b-4 border-[#0EA5E9] -mb-0.5'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-5 h-5 inline-block mr-2" />
              Resources ({resources.length})
            </button>
            <button
              onClick={() => setActiveTab('teacher-tips')}
              className={`pb-4 px-6 font-semibold transition-all ${
                activeTab === 'teacher-tips'
                  ? 'text-[#0EA5E9] border-b-4 border-[#0EA5E9] -mb-0.5'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Lightbulb className="w-5 h-5 inline-block mr-2" />
              Teacher Tips ({teachingTips.length})
            </button>
          </div>

          {/* Add Button */}
          <div className="mb-6">
            <button
              onClick={() => openAddModal(activeTab === 'resources' ? 'resource' : 'teaching-tip')}
              className="bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-all duration-300 rounded-full font-bold shadow-[0_4px_0_0_#0284C7] hover:shadow-[0_2px_0_0_#0284C7] hover:translate-y-[2px] px-6 py-3 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add {activeTab === 'resources' ? 'Resource' : 'Teaching Tip'}
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0EA5E9] mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {activeTab === 'resources' ? (
                resources.map((resource, index) => (
                  <div
                    key={resource.resource_id}
                    className="bg-white rounded-3xl p-6 border-4 border-gray-100 shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium tracking-wide text-[#0EA5E9] bg-[#0EA5E9]/10 px-3 py-1 rounded-full">
                        {resource.category.replace('_', ' ')}
                      </span>
                      <span className="text-sm font-medium tracking-wide text-[#FB7185] bg-[#FB7185]/10 px-3 py-1 rounded-full">
                        {resource.grade_level}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                      {resource.title}
                    </h3>
                    
                    <p className="text-base text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>
                    
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit('resource', resource)}
                          className="flex-1 bg-[#FDE047] text-[#1F2937] hover:bg-[#EAB308] transition-all duration-300 rounded-full font-semibold px-4 py-2 text-sm inline-flex items-center justify-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete('resource', resource.resource_id)}
                          className="bg-[#FB7185] text-white hover:bg-[#E11D48] transition-all duration-300 rounded-full font-semibold px-4 py-2 text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => openNotificationModal(resource)}
                        className="w-full bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-all duration-300 rounded-full font-semibold px-4 py-2 text-sm inline-flex items-center justify-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Send Notification
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                teachingTips.map((tip, index) => (
                  <div
                    key={tip.article_id}
                    className="bg-white rounded-3xl p-6 border-4 border-gray-100 shadow-sm"
                  >
                    {tip.featured_image && (
                      <img 
                        src={tip.featured_image} 
                        alt={tip.title}
                        className="w-full h-40 object-cover rounded-2xl mb-4"
                      />
                    )}
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium tracking-wide text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
                        {tip.category.replace('_', ' ')}
                      </span>
                      {tip.is_featured && (
                        <span className="text-sm font-medium tracking-wide text-amber-600 bg-amber-100 px-3 py-1 rounded-full">
                          Featured
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                      {tip.title}
                    </h3>
                    
                    <p className="text-base text-gray-600 mb-4 line-clamp-2">
                      {tip.excerpt}
                    </p>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit('teaching-tip', tip)}
                        className="flex-1 bg-[#FDE047] text-[#1F2937] hover:bg-[#EAB308] transition-all duration-300 rounded-full font-semibold px-4 py-2 text-sm inline-flex items-center justify-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete('teaching-tip', tip.article_id)}
                        className="bg-[#FB7185] text-white hover:bg-[#E11D48] transition-all duration-300 rounded-full font-semibold px-4 py-2 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>
      
      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingItem ? 'Edit' : 'Add'} {contentType === 'resource' ? 'Resource' : 'Teaching Tip'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {contentType === 'resource' ? (
              // RESOURCE FORM
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Title *</label>
                  <input
                    type="text"
                    value={resourceData.title}
                    onChange={(e) => setResourceData({ ...resourceData, title: e.target.value })}
                    required
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Description *</label>
                  <textarea
                    value={resourceData.description}
                    onChange={(e) => setResourceData({ ...resourceData, description: e.target.value })}
                    required
                    rows={3}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Category *</label>
                    <select
                      value={resourceData.category}
                      onChange={(e) => setResourceData({ ...resourceData, category: e.target.value })}
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    >
                      {resourceCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Year Level *</label>
                    <select
                      value={resourceData.grade_level}
                      onChange={(e) => setResourceData({ ...resourceData, grade_level: e.target.value })}
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    >
                      <option value="Year 2">Year 2</option>
                      <option value="Year 3">Year 3</option>
                      <option value="Year 4">Year 4</option>
                      <option value="Year 5">Year 5</option>
                      <option value="Year 6">Year 6</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Subject</label>
                    <input
                      type="text"
                      value={resourceData.subject}
                      onChange={(e) => setResourceData({ ...resourceData, subject: e.target.value })}
                      placeholder="e.g., Math, Science, English"
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Publisher / Book Name</label>
                    <input
                      type="text"
                      value={resourceData.publisher}
                      onChange={(e) => setResourceData({ ...resourceData, publisher: e.target.value })}
                      placeholder="e.g., Oxford, Cambridge"
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Chapter / Topic</label>
                  <input
                    type="text"
                    value={resourceData.chapter_topic}
                    onChange={(e) => setResourceData({ ...resourceData, chapter_topic: e.target.value })}
                    placeholder="e.g., Chapter 5: Fractions"
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Upload Files (PDF, Word, or Image) * - Multiple files supported
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    Upload all files at once: Student PDFs, Student DOCX, Answer Key PDFs, Answer Key DOCX, etc.
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#0EA5E9] file:text-white hover:file:bg-[#0284C7]"
                  />
                  {uploading && <div className="text-sm text-gray-600 mt-2">Uploading...</div>}
                  
                  {/* Display uploaded files */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-sm font-semibold text-green-600 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {uploadedFiles.length} file(s) ready
                      </div>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-green-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {file.type ? file.type.toUpperCase() : 'FILE'} • {(file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newFiles = uploadedFiles.filter((_, i) => i !== index);
                                setUploadedFiles(newFiles);
                              }}
                              className="text-red-600 hover:text-red-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={resourceData.is_premium}
                      onChange={(e) => setResourceData({ ...resourceData, is_premium: e.target.checked })}
                      className="w-5 h-5 rounded border-2 border-gray-200 text-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20"
                    />
                    <span className="text-sm font-semibold text-gray-900">Premium Resource</span>
                  </label>
                </div>
              </>
            ) : (
              // TEACHING TIP FORM
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Article Title *</label>
                  <input
                    type="text"
                    value={tipData.title}
                    onChange={(e) => setTipData({ ...tipData, title: e.target.value })}
                    required
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Featured Image (optional)</label>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".png,.jpg,.jpeg,.webp"
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#0EA5E9] file:text-white hover:file:bg-[#0284C7]"
                  />
                  {uploading && <div className="text-sm text-gray-600 mt-2">Uploading...</div>}
                  {tipData.featured_image && (
                    <div className="mt-2">
                      <img src={tipData.featured_image} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Short Excerpt (2-3 lines) *</label>
                  <textarea
                    value={tipData.excerpt}
                    onChange={(e) => setTipData({ ...tipData, excerpt: e.target.value })}
                    required
                    rows={2}
                    placeholder="Brief summary that appears on the card..."
                    className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Full Article Content *</label>
                  <TiptapEditor
                    key={editingItem?.article_id || 'new-article'}
                    content={tipData.content || ''}
                    onChange={(html) => setTipData({ ...tipData, content: html })}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Use the toolbar to format your article with headings, bold, italic, lists, and more.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Category *</label>
                    <select
                      value={tipData.category}
                      onChange={(e) => setTipData({ ...tipData, category: e.target.value })}
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    >
                      {tipCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Reading Time (minutes)</label>
                    <input
                      type="number"
                      value={tipData.reading_time}
                      onChange={(e) => setTipData({ ...tipData, reading_time: parseInt(e.target.value) || 5 })}
                      min="1"
                      max="30"
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Subject (optional)</label>
                    <input
                      type="text"
                      value={tipData.subject}
                      onChange={(e) => setTipData({ ...tipData, subject: e.target.value })}
                      placeholder="e.g., Math, Science, General"
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Grade Range</label>
                    <select
                      value={tipData.grade_range}
                      onChange={(e) => setTipData({ ...tipData, grade_range: e.target.value })}
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none"
                    >
                      <option value="All Grades">All Grades</option>
                      <option value="3-5">Grades 3-5</option>
                      <option value="3">Grade 3</option>
                      <option value="4">Grade 4</option>
                      <option value="5">Grade 5</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tipData.is_featured}
                      onChange={(e) => setTipData({ ...tipData, is_featured: e.target.checked })}
                      className="w-5 h-5 rounded border-2 border-gray-200 text-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20"
                    />
                    <span className="text-sm font-semibold text-gray-900">Featured Article</span>
                  </label>
                </div>
              </>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full font-bold py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : `${editingItem ? 'Update' : 'Publish'} ${contentType === 'resource' ? 'Resource' : 'Article'}`}
              </Button>
              <Button
                type="button"
                onClick={() => setShowModal(false)}
                variant="outline"
                className="px-6 rounded-full font-bold"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Resource Notification Modal */}
      <Dialog open={showNotificationModal} onOpenChange={setShowNotificationModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              📧 Send Resource Notification
            </DialogTitle>
          </DialogHeader>
          
          {notificationResource && (
            <div className="space-y-6">
              {/* Resource Info */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-1">{notificationResource.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{notificationResource.description}</p>
              </div>
              
              {/* Audience Selection */}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-3">
                  <Users className="w-4 h-4 inline mr-2" />
                  Select Audience (who will receive this email)
                </label>
                <p className="text-xs text-slate-600 mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  ⚠️ Only subscribers who opted in via the newsletter form will receive this. Registration alone does not grant marketing consent.
                </p>
                <div className="space-y-2 bg-white border border-slate-200 rounded-lg p-4">
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedAudience.includes(22628254)}
                      onChange={() => handleAudienceToggle(22628254)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Newsletter Subscribers</span>
                      <p className="text-xs text-slate-600">Everyone who opted in via homepage newsletter form</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedAudience.includes(22628260)}
                      onChange={() => handleAudienceToggle(22628260)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Teachers (All Tiers)</span>
                      <p className="text-xs text-slate-600">Opted-in teachers (Plus + Pro combined)</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedAudience.includes(22628262)}
                      onChange={() => handleAudienceToggle(22628262)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Teacher Plus Only</span>
                      <p className="text-xs text-slate-600">Opted-in Teacher Plus subscribers only</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedAudience.includes(22628263)}
                      onChange={() => handleAudienceToggle(22628263)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Teacher Pro Only</span>
                      <p className="text-xs text-slate-600">Opted-in Teacher Pro subscribers only</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedAudience.includes(22628269)}
                      onChange={() => handleAudienceToggle(22628269)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Parents</span>
                      <p className="text-xs text-slate-600">Opted-in parent accounts</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Preview Section */}
              {notificationPreview && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-bold text-slate-900 mb-2">📧 Email Preview</h4>
                  <div className="bg-white rounded p-4 space-y-2">
                    <p className="text-sm"><strong>Subject:</strong> {notificationPreview.subject}</p>
                    <p className="text-sm"><strong>To:</strong> {selectedAudience.length} audience group(s)</p>
                    <div className="mt-3 p-3 bg-slate-50 rounded text-xs">
                      <p className="font-mono text-slate-700 whitespace-pre-wrap">
                        {notificationPreview.text_content?.substring(0, 300)}...
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={previewNotification}
                  disabled={selectedAudience.length === 0}
                  className="flex-1 bg-slate-600 hover:bg-slate-700 text-white rounded-full font-bold py-6"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Email
                </Button>
                <Button
                  type="button"
                  onClick={sendNotification}
                  disabled={selectedAudience.length === 0 || sendingNotification}
                  className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full font-bold py-6 disabled:opacity-50"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingNotification ? 'Sending...' : 'Send Notification'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowNotificationModal(false)}
                  variant="outline"
                  className="px-6 rounded-full font-bold"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
}

export default AdminDashboard;
